import { fromJS } from "immutable";
import Nmis from "../nmis";
import {
    ActionReload, ActionSetGloablValue, ActionShowHidden, ActionAlert, ActionSetValue,
    ActionConfirmDialog, ActionEnabledDisabled, ActionForOf, ActionForIn, ActionBreak,
    ActionContinue, ActionSwitch, ActionParallel, ActionAjax, ActionMethod, MethodProp
} from "../types";
import filterTool from "../util/filterTool";
import { NmisApi } from "../api/nmisApi";

export class Action {

    static doActions(actions, data, componentIdMap, sender: Nmis, callback?: Function) {
        if (filterTool.isNull(actions)) {
            if (callback) callback();
            return;
        }

        let index = 0;

        const nextDoAction = () => {
            index += 1;
            // 正常终止
            if (actions.length == index) {
                if (callback) callback();
                return;
            }

            // Continue\Break 时终止
            const action = actions[index];
            if (action.actionType == 'continue' || action.actionType == 'break') {
                if (callback) callback(action.actionType);
                return;
            }

            doAction();
        }

        const doAction = async () => {
            const action = actions[index];
            if(filterTool.isNull(action)) {
                return;
            }
            if (!filterTool.isNull(action.expression) && filterTool.autoFilterValue(action.expression, data) == false) {
                return;
            }

            const eventData = Action.getEventData(data, action.args);

            // 回调方法
            const completeCallback = () => {
                const root = Action.getRootComponent(componentIdMap);

                root.$nextTick(() => {
                    nextDoAction();
                });
            };

            switch (action.actionType) {
                case "reload":
                    await Action.doActionReload(action, eventData, componentIdMap, sender, completeCallback);
                    break;
                case "setGloablValue":
                    await Action.doActionSetGloablValue(action, eventData, componentIdMap, sender, completeCallback);
                    break;
                case "show":
                case "hidden":
                case "mutex":
                    await Action.doActionShowHidden(action, eventData, componentIdMap, sender, completeCallback);
                    break;
                case "alert":
                    await Action.doActionAlert(action, eventData, componentIdMap, sender, completeCallback);
                    break;
                case "setValue":
                    await Action.doActionSetValue(action, eventData, componentIdMap, sender, completeCallback);
                    break;
                case "confirmDialog":
                    await Action.doActionConfirmDialog(action, eventData, componentIdMap, sender, completeCallback);
                    break;
                case "enabled":
                case "disabled":
                    await Action.doActionEnabledDisabled(action, eventData, componentIdMap, sender, completeCallback);
                    break;
                case "forOf":
                    await Action.doActionForOf(action, eventData, componentIdMap, sender, completeCallback);
                    break;
                case "forIn":
                    await Action.doActionForIn(action, eventData, componentIdMap, sender, completeCallback);
                    break;
                case "parallel":
                    await Action.doActionParallel(action, eventData, componentIdMap, sender, completeCallback);
                    break;
                case "switch":
                    await Action.doActionSwitch(action, eventData, componentIdMap, sender, completeCallback);
                    break;
                case "ajax":
                    await Action.doActionAjax(action, eventData, componentIdMap, sender, completeCallback);
                    break;
                case "method":
                    await Action.doActionMethod(action, eventData, componentIdMap, sender, completeCallback);
                    break;
            }
        }

        doAction();
    }

    /**
     * 获取根组件
     * @param componentIdMap 
     * @returns 
     */
    static getRootComponent(componentIdMap): Nmis {
        return componentIdMap.root;
    }

    /**
     * 判断是否有组件
     * @param componentId 
     * @param componentIdMap 
     * @returns 
     */
    static hasComponent(componentId, componentIdMap) {
        if (filterTool.isNull(componentId)) {
            return false;
        }

        if (filterTool.isNull(componentIdMap[componentId])) {
            return false;
        }

        return true;
    }

    static getEventData(data, args) {
        return { event: { data: Object.assign({}, data, args) } }
    }

    /**
     * reload
     * @param action 
     * @param data 
     * @param componentIdMap 
     * @returns 
     */
    static async doActionReload(action: ActionReload, data, componentIdMap, sender: Nmis, completeCallback) {
        if (!Action.hasComponent(action.componentId, componentIdMap)) {
            if (completeCallback) completeCallback();
            return;
        }

        const nmis: Nmis = componentIdMap[action.componentId];
        await nmis.decorator!.loadData(data);
        if (completeCallback) completeCallback();
    }

    /**
     * setGloabValue
     * @param action 
     * @param data 
     * @param componentIdMap 
     * @returns 
     */
    static async doActionSetGloablValue(action: ActionSetGloablValue, data, componentIdMap, sender: Nmis, completeCallback) {
        if (!Action.hasComponent(action.componentId, componentIdMap)) {
            if (completeCallback) completeCallback();
            return;
        }

        const nmis: Nmis = componentIdMap[action.componentId];
        await nmis.decorator!.setGloablValue(action.key, action.value, action.isOnlyAddSetValue, data);
        if (completeCallback) completeCallback();
    }

    /**
     * show hide mutex
     * @param action 
     * @param data 
     * @param componentIdMap 
     * @returns 
     */
    static async doActionShowHidden(action: ActionShowHidden, data, componentIdMap, sender: Nmis, completeCallback) {
        if (!Action.hasComponent(action.componentId, componentIdMap)) {
            if (completeCallback) completeCallback();
            return;
        }

        const nmis: Nmis = componentIdMap[action.componentId];
        await nmis.decorator!.showHidden(action.actionType);
        if (completeCallback) completeCallback();
    }

    /**
     * alert
     * @param action 
     * @param data 
     * @param componentIdMap 
     */
    static doActionAlert(action: ActionAlert, data, componentIdMap, sender: Nmis, completeCallback) {
        const nmis: Nmis = Action.getRootComponent(componentIdMap);
        const msg: string = filterTool.autoFilterValue(action.msg, data);
        switch (action.alertType) {
            case 'success':
                nmis.success(msg);
                break;
            case 'warning':
                nmis.warning(msg);
                break;
            case 'error':
                nmis.error(msg);
                break;
        }
        if (completeCallback) completeCallback();
    }

    /**
     * setValue
     * @param action 
     * @param data 
     * @param componentIdMap 
     * @returns 
     */
    static async doActionSetValue(action: ActionSetValue, data, componentIdMap, sender: Nmis, completeCallback) {
        if (!Action.hasComponent(action.componentId, componentIdMap)) {
            if (completeCallback) completeCallback();
            return;
        }

        const nmis: Nmis = componentIdMap[action.componentId];
        await nmis.decorator!.setValue(action.value, data);
        if (completeCallback) completeCallback();
    }

    /**
     * confirmDialog
     * @param action 
     * @param data 
     * @param componentIdMap 
     */
    static async doActionConfirmDialog(action: ActionConfirmDialog, eventData, componentIdMap, sender: Nmis, completeCallback) {
        const nmis: Nmis = Action.getRootComponent(componentIdMap);
        const title: string = filterTool.autoFilterValue(action.title, eventData);
        const msg: string = filterTool.autoFilterValue(action.msg, eventData);
        const okText: string = filterTool.autoFilterValue(action.okText, eventData);
        const cancelText: string = filterTool.autoFilterValue(action.cancelText, eventData);

        nmis.confirm(msg, () => {
            Action.doActions(action.okActions, eventData.event.data, sender, componentIdMap);
        }, () => {
            Action.doActions(action.cancelActions, eventData.event.data, sender, componentIdMap);
        },
            filterTool.isNullDefault(okText, "确认"),
            filterTool.isNullDefault(cancelText, "取消"),
            filterTool.isNullDefault(title, "提示")
        );
        if (completeCallback) completeCallback();
    }

    /**
     * enabled disabled mutex
     * @param action 
     * @param data 
     * @param componentIdMap 
     * @returns 
     */
    static async doActionEnabledDisabled(action: ActionEnabledDisabled, data, componentIdMap, sender: Nmis, completeCallback) {
        if (!Action.hasComponent(action.componentId, componentIdMap)) {
            if (completeCallback) completeCallback();
            return;
        }

        const nmis: Nmis = componentIdMap[action.componentId];
        await nmis.decorator!.enableOrDisable(action.actionType);
        if (completeCallback) completeCallback();
    }

    /**
     * for of
     * @param action 
     * @param data 
     * @param componentIdMap 
     * @param completeCallback 
     */
    static async doActionForOf(action: ActionForOf, data, componentIdMap, sender: Nmis, completeCallback) {
        if (filterTool.isNull(action.children) || action.children.length == 0) {
            if (completeCallback) completeCallback();
            return;
        }

        const forData = filterTool.autoFilterValue(fromJS(action.forData).toJS(), data);
        filterTool.autoFilterObject(forData, action.forData, data);

        let index = 0;

        const nextDoFor = () => {
            index += 1;
            if (forData.length == index) { // 正常终止
                if (completeCallback) completeCallback();
                return;
            }

            doFor();
        }

        const doFor = () => {
            const item = forData[index];
            Action.doActions(action.children, item, componentIdMap, sender, (type) => {
                if (type == 'break') { // 终止循环
                    if (completeCallback) completeCallback();
                    return;
                }
                nextDoFor();
            });
        }

        doFor();
    }

    /**
     * for in
     * @param action 
     * @param data 
     * @param componentIdMap 
     * @param completeCallback 
     */
    static async doActionForIn(action: ActionForIn, data, componentIdMap, sender: Nmis, completeCallback) {
        if (filterTool.isNull(action.children) || action.children.length == 0) {
            if (completeCallback) completeCallback();
            return;
        }

        const forData = filterTool.autoFilterValue(fromJS(action.forData).toJS(), data);
        filterTool.autoFilterObject(forData, action.forData, data);

        let index = 0;
        const keys = Object.keys(forData);

        const nextDoFor = () => {
            index += 1;
            if (keys.length == index) { // 正常终止
                if (completeCallback) completeCallback();
                return;
            }

            doFor();
        }

        const doFor = () => {
            const key = keys[index];
            Action.doActions(action.children, { key: key, value: forData[key] }, componentIdMap, sender, (type) => {
                if (type == 'break') { // 终止循环
                    if (completeCallback) completeCallback();
                    return;
                }
                nextDoFor();
            });
        }

        doFor();
    }

    /**
     * parallel 并行执行
     * @param action 
     * @param data 
     * @param componentIdMap 
     * @param completeCallback 
     * @returns 
     */
    static async doActionParallel(action: ActionParallel, data, componentIdMap, sender: Nmis, completeCallback) {
        if (filterTool.isNull(action.children) || action.children.length == 0) {
            if (completeCallback) completeCallback();
            return;
        }

        for (const item of action.children) {
            Action.doActions([item], data, componentIdMap, sender);
        }

        if (completeCallback) completeCallback();
    }

    /**
     * switch
     * @param action 
     * @param data 
     * @param componentIdMap 
     * @param completeCallback 
     * @returns 
     */
    static async doActionSwitch(action: ActionSwitch, data, componentIdMap, sender: Nmis, completeCallback) {
        const switchValue = filterTool.autoFilterValue(action.switchValue, data);

        let defaultCase: any[] = [];
        let runDefaultCase: boolean = true;

        for (const actionCase of action.cases) {
            if (actionCase.case.indexOf(switchValue) != -1) {
                runDefaultCase = false;
                Action.doActions(actionCase.children, data, componentIdMap, sender, () => {
                    if (completeCallback) completeCallback();
                });
                break;
            }

            if (actionCase.case.indexOf('default') != -1) {
                defaultCase = actionCase.children;
            }
        }

        // 运行默认case
        if (runDefaultCase && defaultCase.length > 0) {
            Action.doActions(defaultCase, data, componentIdMap, sender, () => {
                if (completeCallback) completeCallback();
            });
            return;
        }

        if (completeCallback) completeCallback();

    }

    /**
     * ajax
     * @param action 
     * @param data 
     * @param componentIdMap 
     * @param sender 
     * @param completeCallback 
     * @returns 
     */
    static async doActionAjax(action: ActionAjax, data, componentIdMap, sender: Nmis, completeCallback) {
        const root: Nmis = Action.getRootComponent(componentIdMap);

        // 发送请求
        const apiResult = await NmisApi.send(action.api, data, root.projectId, root.childsysId, root.devProd);
        if (apiResult == undefined) {
            if (completeCallback) completeCallback();
            return;
        }

        const resultData = root.parseApiResult(apiResult);

        // 将值绑定到outputVar参数上
        if (!filterTool.isNull(action.outputVar)) {
            let value = {};
            value[action.outputVar] = resultData;
            sender.decorator!.setValue(value, {});
        }

        if (completeCallback) completeCallback();
    }

    /**
     * method
     * @param action 
     * @param data 
     * @param componentIdMap 
     * @param sender 
     * @param completeCallback 
     * @returns 
     */
    static async doActionMethod(action: ActionMethod, data, componentIdMap, sender: Nmis, completeCallback) {
        if (filterTool.isNull(sender.methodObj) || !action.method.isMethod) {
            if (completeCallback) completeCallback();
            return;
        }

        const method = sender.getMethod(action.method.methodName);
        if (filterTool.isNull(method)) {
            if (completeCallback) completeCallback();
            return;
        }

        /**
         * 构件方法参数
         */
        const methodParam: MethodProp = {
            sender: sender,
            param: Object.assign({
                action: action,
                completeCallback: () => {
                    if (completeCallback) completeCallback();
                }
            }, data)
        }

        method(methodParam);
    }
} 